import React from "react";
import ProtectedRoute from "../../../../protected-route/ProtectedRoute";
import TspLayout from "../../layouts/TspLayout";
import useTspPermission from "../../hooks/useTspPermission";

// Composes the existing ICC ProtectedRoute (the login/session gate — reused
// as-is, not duplicated or rewritten) with a TSP permission check. Mirrors
// ProtectedRoute's own usage convention:
//   <TspProtectedRoute permission={TSP_PERMISSIONS.JOURNEYS_VIEW} children={<TspLayout><TspJourneysPage /></TspLayout>} />
//
// This only controls what renders in the browser. The backend's
// requireTspAuth + requireTspPermission (brihadmrdanga-backend
// modules/tsp/middlewares) is what actually enforces access — this check
// existing alone is not security.
export default function TspProtectedRoute({ permission, children }) {
  return <ProtectedRoute children={<TspPermissionGate permission={permission}>{children}</TspPermissionGate>} />;
}

function TspPermissionGate({ permission, children }) {
  const { hasPermission, loading } = useTspPermission();

  // The gate sits OUTSIDE the page's own <TspLayout> (see App.js), so its
  // loading / denied states must render their own TspLayout: otherwise a denied
  // route would replace the whole screen — header and primary nav included —
  // with a bare line of text. The primary nav is application navigation and has
  // to stay on screen even where the page itself is off-limits. When access is
  // granted the page's own <TspLayout> takes the same position in the tree, so
  // the header is not remounted.

  // Access is resolved asynchronously (GET /api/tsp/me) — wait rather than
  // flashing "access denied" before the real permission set has loaded.
  if (loading) {
    return (
      <TspLayout>
        <div className="tsp-loading">Checking TSP access…</div>
      </TspLayout>
    );
  }

  if (permission && !hasPermission(permission)) {
    return (
      <TspLayout>
        <div className="tsp-access-denied">You do not have access to this TSP section.</div>
      </TspLayout>
    );
  }

  return children;
}
